import {
  AttachmentBlockSchema,
  FootNoteReferenceParamsSchema,
} from '@blocksuite/affine-model';
import {
  BlockMarkdownAdapterExtension,
  type BlockMarkdownAdapterMatcher,
  FOOTNOTE_DEFINITION_PREFIX,
  FULL_FILE_PATH_KEY,
  getFootnoteDefinitionText,
  getImageFullPath,
  isFootnoteDefinitionNode,
  type MarkdownAST,
} from '@blocksuite/affine-shared/adapters';
import { getAssetName, nanoid } from '@blocksuite/store';

const isAttachmentFootnoteDefinitionNode = (node: MarkdownAST) => {
  if (!isFootnoteDefinitionNode(node)) return false;
  const footnoteDefinition = getFootnoteDefinitionText(node);
  try {
    const footnoteDefinitionJson = FootNoteReferenceParamsSchema.parse(
      JSON.parse(footnoteDefinition)
    );
    return (
      footnoteDefinitionJson.type === 'attachment' &&
      !!footnoteDefinitionJson.blobId
    );
  } catch {
    return false;
  }
};

export const attachmentBlockMarkdownAdapterMatcher: BlockMarkdownAdapterMatcher =
  {
    flavour: AttachmentBlockSchema.model.flavour,
    toMatch: o => isAttachmentFootnoteDefinitionNode(o.node),
    fromMatch: o => o.node.flavour === AttachmentBlockSchema.model.flavour,
    toBlockSnapshot: {
      enter: (o, context) => {
        if (!isFootnoteDefinitionNode(o.node)) {
          return;
        }

        const { walkerContext, configs, assets } = context;
        const footnoteIdentifier = o.node.identifier;
        const footnoteDefinitionKey = `${FOOTNOTE_DEFINITION_PREFIX}${footnoteIdentifier}`;
        const footnoteDefinition = configs.get(footnoteDefinitionKey);
        if (!footnoteDefinition) {
          return;
        }
        try {
          const footnoteDefinitionJson = FootNoteReferenceParamsSchema.parse(
            JSON.parse(footnoteDefinition)
          );
          const { blobId, fileName, url } = footnoteDefinitionJson;
          if (!blobId || !fileName) {
            walkerContext.skipAllChildren();
            return;
          }
          // The export records the canonical archive path in the footnote.
          // Keep its original blob ID, which the inline reference also uses.
          if (assets && url) {
            const path = getImageFullPath(
              configs.get(FULL_FILE_PATH_KEY) ?? '',
              url
            );
            const archiveBlobId = assets.getBlobId(path);
            const blob = archiveBlobId
              ? assets.getAsset(archiveBlobId)
              : undefined;
            if (blob && archiveBlobId !== blobId)
              assets.getAssets().set(blobId, blob);
          }
          // With imported assets, conversion writes each attachment to blob
          // storage. Skip an unavailable file; plain Markdown has no assets and
          // can still refer to an existing workspace blob without rewriting it.
          if (assets && !assets.isEmpty() && !assets.getAsset(blobId)) {
            walkerContext.skipAllChildren();
            return;
          }
          walkerContext
            .openNode(
              {
                type: 'block',
                id: nanoid(),
                flavour: AttachmentBlockSchema.model.flavour,
                props: {
                  name: fileName,
                  sourceId: blobId,
                  footnoteIdentifier,
                  style: 'citation',
                },
                children: [],
              },
              'children'
            )
            .closeNode();
          walkerContext.skipAllChildren();
        } catch (err) {
          console.warn('Failed to parse attachment footnote definition:', err);
          return;
        }
      },
    },
    fromBlockSnapshot: {
      enter: async (o, context) => {
        const { assets, configs, updateAssetIds, walkerContext } = context;
        const blobId = o.node.props.sourceId as string | undefined;
        if (!assets || !blobId) return;
        await assets.readFromBlob(blobId);
        const blob = assets.getAssets().get(blobId);
        if (!blob) return;
        const name = getAssetName(assets.getAssets(), blobId);
        updateAssetIds?.(blobId);
        const footnoteIdentifier = o.node.props.footnoteIdentifier as
          | string
          | undefined;
        const identifier = footnoteIdentifier || o.node.id;
        const key = FOOTNOTE_DEFINITION_PREFIX + identifier;
        const definition = configs.get(key);
        const reference = definition
          ? FootNoteReferenceParamsSchema.parse(JSON.parse(definition))
          : {
              type: 'attachment',
              blobId,
              fileName: o.node.props.name,
              fileType: blob.type,
            };
        configs.set(
          key,
          JSON.stringify({
            ...reference,
            url: encodeURIComponent('assets/' + name),
          })
        );
        if (!footnoteIdentifier) {
          walkerContext
            .openNode({ type: 'paragraph', children: [] }, 'children')
            .openNode(
              { type: 'footnoteReference', identifier, label: identifier },
              'children'
            )
            .closeNode()
            .closeNode();
        }
      },
    },
  };

export const AttachmentBlockMarkdownAdapterExtension =
  BlockMarkdownAdapterExtension(attachmentBlockMarkdownAdapterMatcher);
